import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStrategicTree, getLinkedExecutionActions } from '../services/hoshinService';
import { ProjectService } from '../services/projectService';
import { IndicatorService } from '../services/indicatorService';
import {
  buildHoshinDashboardData,
  logHoshinDashboardCounts,
  type HoshinDashboardData,
} from '../services/hoshinDashboardData';
import type { ProjectHubIssue } from '../services/projectService';
import type { OrgMember } from '../services/organizationService';

export function useHoshinDashboardData(
  organizationId: string | undefined,
  members: OrgMember[],
  departmentMap: Map<string, string>,
): {
  data: HoshinDashboardData | null;
  isLoading: boolean;
  refetch: () => void;
} {
  const loggedRef = useRef<string | null>(null);

  const { data: tree = [], isLoading: treeLoading, refetch: refetchTree } = useQuery({
    queryKey: ['hoshin-tree', organizationId],
    queryFn: () => getStrategicTree(organizationId!),
    enabled: !!organizationId,
    staleTime: 30_000,
  });

  const { data: actions = [], isLoading: actionsLoading, refetch: refetchActions } = useQuery({
    queryKey: ['hoshin-linked-actions', organizationId],
    queryFn: () => getLinkedExecutionActions(organizationId!),
    enabled: !!organizationId,
    staleTime: 30_000,
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['hoshin-projects', organizationId],
    queryFn: () => ProjectService.list(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  const { data: indicators = [], isLoading: indicatorsLoading } = useQuery({
    queryKey: ['hoshin-indicators', organizationId],
    queryFn: () => IndicatorService.list(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  const projectIds = useMemo(() => {
    const ids = new Set<string>();
    for (const action of actions) {
      if (action.project_id) ids.add(action.project_id);
    }
    return [...ids];
  }, [actions]);

  const { data: issuesByProject = new Map<string, ProjectHubIssue[]>(), isLoading: issuesLoading } = useQuery({
    queryKey: ['hoshin-project-issues', organizationId, projectIds.join(',')],
    queryFn: async () => {
      const map = new Map<string, ProjectHubIssue[]>();
      await Promise.all(projectIds.map(async (projectId) => {
        const issues = await ProjectService.listProjectIssues(organizationId!, projectId);
        map.set(projectId, issues);
      }));
      return map;
    },
    enabled: !!organizationId && projectIds.length > 0,
    staleTime: 60_000,
  });

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      map.set(member.user_id, member.user_name ?? member.user_id.slice(0, 8));
    }
    return map;
  }, [members]);

  const dashboard = useMemo(() => {
    if (!organizationId) return null;
    return buildHoshinDashboardData({
      tree,
      actions,
      projects,
      indicators,
      issuesByProject,
      memberNameById,
      departmentNameById: departmentMap,
    });
  }, [organizationId, tree, actions, projects, indicators, issuesByProject, memberNameById, departmentMap]);

  useEffect(() => {
    if (!dashboard) return;
    const fingerprint = [
      dashboard.summary.averageProbability,
      dashboard.summary.healthyGoals,
      dashboard.summary.criticalGoals,
      dashboard.summary.overdueActions,
    ].join(':');
    if (loggedRef.current === fingerprint) return;
    logHoshinDashboardCounts(dashboard);
    loggedRef.current = fingerprint;
  }, [dashboard]);

  const refetch = () => {
    void refetchTree();
    void refetchActions();
  };

  return {
    data: dashboard,
    isLoading: treeLoading || actionsLoading || projectsLoading || indicatorsLoading || issuesLoading,
    refetch,
  };
}
